import React, { useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import styled, { css, useTheme } from 'styled-components';
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionItemBodyWithAnimation,
  AccordionItemHeading,
  RawHtml,
  Text,
} from '@sparknz/spark-pattern-library';
import { LinkRegion, trackCustomLink } from '@sparknz/tracking';

const Title = styled(Text)`
  ${({ theme: { media, spacing } }) => css`
    margin: 0 auto ${spacing.s4};
    width: 100%;
    text-align: center;

    ${media.up('big')} {
      margin-bottom: ${spacing.s7};
    }
  `}
`;

interface RichTextContentAccordionItem {
  readonly name: string;
  readonly content: string;
}

interface RichTextContentAccordionProps {
  title: string;
  richTextContent: readonly RichTextContentAccordionItem[];
  description?: string;
}

const RichTextContentAccordion = ({
  title,
  description,
  richTextContent,
}: RichTextContentAccordionProps) => {
  const [currentExpandedItems, setCurrentExpandedItems] = useState<string[]>(
    [],
  );

  const { palette } = useTheme();

  if (isEmpty(richTextContent)) {
    return null;
  }

  return (
    <>
      <LinkRegion regionId="Additional info">
        <Title variant="headingS">{title}</Title>
        <Text variant="bodyMDemi" mb={['s3', 's4', 's4']}>
          {description}
        </Text>
        <Accordion
          allowMultipleExpanded
          onChange={(expandedItems: string[]) => {
            setCurrentExpandedItems(expandedItems);
            trackCustomLink(expandedItems?.[0]);
          }}
        >
          {richTextContent.map(({ name, content }) => {
            // uuid must be a valid HTML5 id but was given "accordion__heading-Compare iPhone models", ASCII whitespaces are forbidden
            const uuidWithoutSpaces = name.replace(/ /g, '-');
            const isOpen = currentExpandedItems.includes(uuidWithoutSpaces);
            return (
              <AccordionItem
                key={uuidWithoutSpaces}
                uuid={uuidWithoutSpaces}
                isOpen={isOpen}
              >
                <AccordionItemHeading>
                  <Text variant="subheadingM">{name}</Text>
                  <AccordionButton />
                </AccordionItemHeading>
                <AccordionItemBodyWithAnimation isOpen={isOpen}>
                  <RawHtml
                    color={palette.baseColors.newZealandBlack}
                    variant="bodySMedium"
                    html={content}
                  />
                </AccordionItemBodyWithAnimation>
              </AccordionItem>
            );
          })}
        </Accordion>
      </LinkRegion>
    </>
  );
};

export default RichTextContentAccordion;
